"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";

import {
    canStartUnit,
    formatResetIn,
    type UsageSummary,
} from "@/lib/usage-limits";

import "./landing.css";

/* ── Types ── */
export type LandingLesson = {
    id: string;
    title: string;
    isText: boolean;
    isQuiz: boolean;
    /** Description copy from the lesson JSON — same text shown in the
     *  lesson-start modal. Optional; falls back to a generated blurb. */
    description?: string;
};

export type LandingUnit = {
    id: string;
    title: string;
    locked: boolean;
    lessons: LandingLesson[];
};

export type LandingProps = {
    units: LandingUnit[];
    completedLessons: string[];
    /** Authoritative server XP. */
    totalXp: number;
    /** Streak (days in a row). */
    streak: number;
    /** Level derived from XP. */
    level: number;
    /** XP earned within the user's current level. */
    xpIntoLevel: number;
    /** XP required to reach the next level (length of current level band). */
    xpForNext: number;
    /** XP earned today. */
    xpToday: number;
    /** Daily XP goal target. */
    xpDailyGoal: number;
    /** Booleans for the last 7 weekday cells (Mon-first). */
    weekActive: boolean[];
    /** Display name for the signed-in user, or null. */
    displayName: string | null;
    /** Rolling unit-usage window for the signed-in user. */
    usage: UsageSummary;
    /** Curriculum lessons assigned to the user by a class they belong to. */
    assignedLessonIds?: string[];
};

type LessonState = "complete" | "current" | "available" | "locked";

/* Per-unit theme rotation. Color values live in landing.css. */
const THEME_COUNT = 9;
const THEMES: { themeIndex: number }[] = Array.from({ length: 18 }, (_, i) => ({
    themeIndex: (i % THEME_COUNT) + 1,
}));

const WEEK_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

/* ── Helpers ── */
function stripUnitPrefix(title: string): string {
    return title.replace(/^Unit \d+:\s*/, "");
}

/**
 * Find the global "current" lesson — the very first uncompleted lesson in
 * the first non-locked unit that still has work left.
 */
function findCurrentLesson(
    units: LandingUnit[],
    completed: Set<string>,
): { unitIdx: number; lessonIdx: number } | null {
    for (let u = 0; u < units.length; u++) {
        const unit = units[u];
        if (unit.locked) continue;
        for (let l = 0; l < unit.lessons.length; l++) {
            if (!completed.has(unit.lessons[l].id)) {
                return { unitIdx: u, lessonIdx: l };
            }
        }
    }
    return null;
}

export default function LandingClient(props: LandingProps) {
    const {
        units,
        completedLessons,
        totalXp,
        streak,
        level,
        xpIntoLevel,
        xpForNext,
        xpToday,
        xpDailyGoal,
        weekActive,
    } = props;
    // displayName is in the props type for back-compat with the page server
    // component, but the global SideNav now owns the user chip.

    const router = useRouter();
    const completedSet = useMemo(() => new Set(completedLessons), [completedLessons]);

    const assignedSet = useMemo(
        () => new Set(props.assignedLessonIds ?? []),
        [props.assignedLessonIds],
    );

    const [usage, setUsage] = useState<UsageSummary>(props.usage);
    const [now, setNow] = useState(() => new Date());
    const [showLimit, setShowLimit] = useState(false);
    const usageRef = useRef(usage);
    usageRef.current = usage;

    useEffect(() => setUsage(props.usage), [props.usage]);

    useEffect(() => {
        const tick = () => {
            setNow(new Date());
            const snapshot = usageRef.current;
            if (!snapshot.limited || !snapshot.resetAt) return;
            if (new Date(snapshot.resetAt).getTime() > Date.now()) return;
            fetch("/api/usage")
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                    if (data?.usage) setUsage(data.usage as UsageSummary);
                })
                .catch(() => {});
        };
        const id = window.setInterval(tick, 30_000);
        return () => window.clearInterval(id);
    }, []);

    useEffect(() => {
        if (!showLimit) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setShowLimit(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showLimit]);

    const unitBlocked = (unitId: string) =>
        usage.limited && !canStartUnit(usage, unitId);

    const usageUsed = usage.limit ? Math.min(usage.used, usage.limit) : usage.used;
    const usageRemaining = usage.remaining ?? 0;
    const usageExhausted = usage.limited && usageRemaining <= 0;
    const resetIn = formatResetIn(usage.resetAt, now);

    const current = useMemo(
        () => findCurrentLesson(units, completedSet),
        [units, completedSet],
    );
    const currentUnitIdx = current?.unitIdx ?? -1;
    const currentLessonIdxInUnit = current?.lessonIdx ?? -1;

    const totalLessonsComplete = completedSet.size;
    const totalUnitsListed = units.length;

    const goalPct = xpDailyGoal > 0
        ? Math.min(100, Math.round((xpToday / xpDailyGoal) * 100))
        : 0;
    const levelPct = xpForNext > 0
        ? Math.min(100, Math.max(0, (xpIntoLevel / xpForNext) * 100))
        : 0;

    const stateFor = (uIdx: number, lIdx: number): LessonState => {
        const unit = units[uIdx];
        if (unit.locked) return "locked";
        const lesson = unit.lessons[lIdx];
        if (completedSet.has(lesson.id)) return "complete";
        if (uIdx === currentUnitIdx && lIdx === currentLessonIdxInUnit) {
            return "current";
        }
        return "available";
    };

    /* Only the in-progress unit is expanded by default. */
    const [expanded, setExpanded] = useState<Set<number>>(() => {
        if (currentUnitIdx >= 0) return new Set([currentUnitIdx]);
        // No "current" lesson (everything done or all locked) — expand the
        // first non-locked unit so the page isn't fully collapsed.
        const firstUnlocked = units.findIndex((u) => !u.locked);
        return new Set(firstUnlocked >= 0 ? [firstUnlocked] : []);
    });

    /* Lesson detail panels — multiple can be open at once (file-explorer feel). */
    const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());

    const toggleUnit = (idx: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });

    const toggleLesson = (lessonId: string) =>
        setExpandedLessons((prev) => {
            const next = new Set(prev);
            if (next.has(lessonId)) next.delete(lessonId);
            else next.add(lessonId);
            return next;
        });

    const launchLesson = (lessonId: string, state: LessonState, unitId: string) => {
        if (state === "locked") return;
        if (unitBlocked(unitId) && !assignedSet.has(lessonId)) {
            setShowLimit(true);
            return;
        }
        router.push(`/lesson?lesson=${encodeURIComponent(lessonId)}&from=path`);
    };

    function lessonTypeLabel(lesson: LandingLesson): string {
        if (lesson.isText) return "Reading";
        if (lesson.isQuiz) return "Quiz";
        return "Practice";
    }

    function lessonDescription(lesson: LandingLesson, lIdx: number, total: number): string {
        // Prefer the authored description from the lesson JSON (same text the
        // lesson-start modal shows). Fall back to a generated blurb so lessons
        // without a description still render something.
        if (lesson.description && lesson.description.trim().length > 0) {
            return lesson.description;
        }
        const position = `Lesson ${lIdx + 1} of ${total}`;
        if (lesson.isText) {
            return `${position}. A short reading covering the concepts behind this lesson. No camera or signing required, so take your time and continue when you're ready.`;
        }
        if (lesson.isQuiz) {
            return `${position}. A timed quiz testing what you've learned in this unit. Each prompt has a fixed time limit, so trust your fingers and keep moving.`;
        }
        return `${position}. Sign the prompted words on camera. Your hand-tracking gives instant feedback, so accuracy matters more than speed.`;
    }

    function ctaLabel(state: LessonState, blocked: boolean): string {
        if (state === "locked") return "Locked";
        if (blocked) return "Unit limit reached";
        if (state === "complete") return "Practice again";
        if (state === "current") return "Continue lesson";
        return "Start lesson";
    }

    const currentUnit = currentUnitIdx >= 0 ? units[currentUnitIdx] : null;
    const currentUnitTheme = currentUnitIdx >= 0
        ? THEMES[currentUnitIdx % THEMES.length]
        : THEMES[0];
    const currentUnitDoneInUnit = currentUnit
        ? currentUnit.lessons.filter((l) => completedSet.has(l.id)).length
        : 0;

    return (
        <div className="l2-shell">
            {/* Ambient glows */}
            <div className="l2-glow l2-glow--a" aria-hidden="true" />
            <div className="l2-glow l2-glow--b" aria-hidden="true" />

            {showLimit && (
                <div
                    className="usage-limit-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Unit limit reached"
                    onClick={() => setShowLimit(false)}
                >
                    <div
                        className="usage-limit-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="usage-limit-close"
                            onClick={() => setShowLimit(false)}
                            aria-label="Close"
                        >
                            ✕
                        </button>

                        <div className="usage-limit-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v5l3 2" />
                            </svg>
                        </div>

                        <div className="usage-limit-eyebrow">Unit limit reached</div>
                        <h2 className="usage-limit-title">
                            You&apos;ve started {usage.limit} units in the last{" "}
                            {usage.windowHours} hours
                        </h2>
                        <p className="usage-limit-desc">
                            Free accounts can start {usage.limit} new units every{" "}
                            {usage.windowHours} hours. You can still practice any lesson
                            inside a unit you&apos;ve already opened.
                        </p>

                        <div className="usage-limit-meter" aria-hidden="true">
                            {Array.from({ length: usage.limit ?? 0 }, (_, i) => (
                                <span
                                    key={i}
                                    className={`usage-limit-slot${
                                        i < usageUsed ? " usage-limit-slot--filled" : ""
                                    }`}
                                />
                            ))}
                        </div>

                        {usage.units.length > 0 && (
                            <ul className="usage-limit-list">
                                {usage.units.map((entry) => {
                                    const unit = units.find((u) => u.id === entry.unitId);
                                    return (
                                        <li key={entry.unitId} className="usage-limit-list-item">
                                            <span className="usage-limit-list-dot" aria-hidden="true" />
                                            <span className="usage-limit-list-title">
                                                {unit ? stripUnitPrefix(unit.title) : entry.unitId}
                                            </span>
                                            <span className="usage-limit-list-meta">
                                                Open for {formatResetIn(
                                                    new Date(
                                                        new Date(entry.startedAt).getTime() +
                                                            usage.windowHours * 3_600_000,
                                                    ).toISOString(),
                                                    now,
                                                )}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}

                        <div className="usage-limit-reset">
                            Your next unit unlocks in <strong>{resetIn}</strong>
                        </div>

                        <a className="usage-limit-cta" href="/plans">
                            Upgrade for unlimited units
                        </a>
                        <button
                            type="button"
                            className="usage-limit-dismiss"
                            onClick={() => setShowLimit(false)}
                        >
                            Keep practicing what&apos;s open
                        </button>
                    </div>
                </div>
            )}

            {/* The left sidebar lives in <SideNavGate> at the layout level so
                it matches every other page exactly. The grid below is now
                two-column (main + rightbar); see .l2-shell in landing.css. */}

            {/* ── MAIN CENTER ── */}
            <main className="l2-main">
                {/* Top sticky bar showing current unit */}
                <div className="l2-topbar">
                    <div className="l2-topbar-left">
                        {currentUnit ? (
                            <>
                                <div
                                    className="l2-topbar-eyebrow"
                                    style={{
                                        color: `var(--u-accent, var(--accent))`,
                                    }}
                                    data-theme={currentUnitTheme.themeIndex}
                                >
                                    Unit {currentUnitIdx + 1} ·
                                    {" "}
                                    {currentUnitDoneInUnit}/{currentUnit.lessons.length} lessons ·
                                    {" "}
                                    in progress
                                </div>
                                <div className="l2-topbar-title">
                                    {stripUnitPrefix(currentUnit.title)}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="l2-topbar-eyebrow">
                                    All caught up
                                </div>
                                <div className="l2-topbar-title">
                                    Pick a lesson to review
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Units + paths */}
                <div className="l2-path-wrap">
                    {units.map((unit, uIdx) => {
                        const theme = THEMES[uIdx % THEMES.length];
                        const isOpen = expanded.has(uIdx);
                        const done = unit.lessons.filter((l) =>
                            completedSet.has(l.id),
                        ).length;
                        const total = unit.lessons.length;
                        const isLockedUnit = unit.locked;
                        const isComplete = !isLockedUnit && done >= total && total > 0;
                        const blocked = !isLockedUnit && unitBlocked(unit.id);

                        const statusText = isLockedUnit
                            ? "Locked · Coming soon"
                            : blocked
                              ? `${done} / ${total} lessons · Limit reached`
                              : isComplete
                                ? "Completed"
                                : `${done} / ${total} lessons`;

                        return (
                            <section
                                key={unit.id}
                                className={`l2-unit l2-unit--theme-${theme.themeIndex} ${
                                    isOpen ? "l2-unit--open" : "l2-unit--closed"
                                } ${isLockedUnit ? "l2-unit--locked" : ""} ${
                                    blocked ? "l2-unit--limited" : ""
                                }`}
                            >
                                {/* Unit banner — click to toggle */}
                                <button
                                    className="l2-unit-banner"
                                    onClick={() => toggleUnit(uIdx)}
                                    aria-expanded={isOpen}
                                >
                                    <div className="l2-unit-banner-inner">
                                        <div className="l2-unit-banner-text">
                                            <div className="l2-unit-banner-eyebrow">
                                                Unit {uIdx + 1}
                                                {isLockedUnit && " · Locked"}
                                                {blocked && (
                                                    <span className="l2-unit-limit-pill">
                                                        <LimitIcon />
                                                        {resetIn ? `Unlocks in ${resetIn}` : "Limit reached"}
                                                    </span>
                                                )}
                                            </div>
                                            <h2 className="l2-unit-banner-title">
                                                {stripUnitPrefix(unit.title)}
                                            </h2>
                                            <div className="l2-unit-banner-meta">
                                                {statusText}
                                            </div>
                                        </div>
                                        <span
                                            className={`l2-unit-chevron ${
                                                isOpen ? "l2-unit-chevron--open" : ""
                                            }`}
                                            aria-hidden="true"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </span>
                                    </div>
                                </button>

                                {/* Lesson tree — file-explorer-style nested rows.
                                    Each lesson row has its own caret that expands a
                                    detail panel beneath it, which has its own caret. */}
                                <div className="l2-path-collapse" aria-hidden={!isOpen}>
                                    <div className="l2-path-collapse-inner">
                                        <div className="l2-lessons">
                                            {unit.lessons.map((lesson, lIdx) => {
                                                const state = stateFor(uIdx, lIdx);
                                                const lessonOpen = expandedLessons.has(lesson.id);
                                                const isLocked = state === "locked";
                                                const lessonBlocked =
                                                    blocked && !assignedSet.has(lesson.id);

                                                return (
                                                    <div
                                                        key={lesson.id}
                                                        className={`l2-lesson-item l2-lesson-item--${state} ${
                                                            lessonOpen ? "l2-lesson-item--open" : ""
                                                        }`}
                                                    >
                                                        <button
                                                            className={`l2-lesson-row l2-lesson-row--${state}`}
                                                            onClick={() => !isLocked && toggleLesson(lesson.id)}
                                                            aria-expanded={lessonOpen}
                                                            aria-label={lesson.title}
                                                            disabled={isLocked}
                                                            tabIndex={isOpen && !isLocked ? 0 : -1}
                                                            title={
                                                                isLocked
                                                                    ? "This unit is still under construction"
                                                                    : lesson.title
                                                            }
                                                        >
                                                            <span
                                                                className={`l2-lesson-row__caret ${
                                                                    lessonOpen ? "l2-lesson-row__caret--open" : ""
                                                                }`}
                                                                aria-hidden="true"
                                                            >
                                                                <CaretRight />
                                                            </span>
                                                            <span className="l2-lesson-row__icon" aria-hidden="true">
                                                                <LessonStateIcon state={state} lesson={lesson} />
                                                            </span>
                                                            <span className="l2-lesson-row__main">
                                                                <span className="l2-lesson-row__title">
                                                                    {lesson.title}
                                                                </span>
                                                                <span className="l2-lesson-row__type">
                                                                    {lessonTypeLabel(lesson)} · Lesson {lIdx + 1}
                                                                </span>
                                                            </span>
                                                            {state === "current" && (
                                                                <span className="l2-lesson-row__pill">Continue</span>
                                                            )}
                                                            {state === "complete" && (
                                                                <span className="l2-lesson-row__pill l2-lesson-row__pill--done">
                                                                    Done
                                                                </span>
                                                            )}
                                                        </button>

                                                        <div
                                                            className="l2-lesson-detail-collapse"
                                                            aria-hidden={!lessonOpen}
                                                        >
                                                            <div className="l2-lesson-detail-collapse-inner">
                                                                <div className="l2-lesson-detail">
                                                                    <button
                                                                        className="l2-lesson-detail__header"
                                                                        onClick={() => toggleLesson(lesson.id)}
                                                                        aria-expanded={lessonOpen}
                                                                    >
                                                                        <span className="l2-lesson-detail__caret" aria-hidden="true">
                                                                            <CaretRight />
                                                                        </span>
                                                                        <span>Lesson details</span>
                                                                    </button>
                                                                    <div className="l2-lesson-detail__body">
                                                                        <p className="l2-lesson-detail__desc">
                                                                            {lessonDescription(
                                                                                lesson,
                                                                                lIdx,
                                                                                unit.lessons.length,
                                                                            )}
                                                                        </p>
                                                                        <button
                                                                            className={`l2-lesson-detail__start${
                                                                                lessonBlocked
                                                                                    ? " l2-lesson-detail__start--limited"
                                                                                    : ""
                                                                            }`}
                                                                            onClick={() =>
                                                                                launchLesson(
                                                                                    lesson.id,
                                                                                    state,
                                                                                    unit.id,
                                                                                )
                                                                            }
                                                                            disabled={isLocked}
                                                                        >
                                                                            <span>{ctaLabel(state, lessonBlocked)}</span>
                                                                            {!isLocked && !lessonBlocked && <ArrowRight />}
                                                                            {lessonBlocked && <LimitIcon />}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        );
                    })}

                    {/* Coming soon stub */}
                    <section className="l2-coming-soon">
                        <div className="l2-coming-soon-inner">
                            <div className="l2-coming-soon-title">More units on the way</div>
                            <div className="l2-coming-soon-sub">
                                We're still building out the full curriculum. Check
                                {" "}
                                <a href="/changelog">the changelog</a> for what's new.
                            </div>
                        </div>
                    </section>
                </div>
            </main>

            {/* ── RIGHT SIDEBAR ── */}
            <aside className="l2-rightbar">
                {usage.limited && (
                    <div
                        className={`l2-stat-card l2-stat-card--usage${
                            usageExhausted ? " l2-stat-card--usage-out" : ""
                        }`}
                    >
                        <div className="l2-stat-card-header">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v5l3 2" />
                            </svg>
                            <span>Unit Usage</span>
                        </div>
                        <div className="l2-usage-row">
                            <span className="l2-usage-count">
                                {usageUsed}
                                <span className="l2-usage-total">/{usage.limit}</span>
                            </span>
                            <span className="l2-usage-window">
                                per {usage.windowHours}h
                            </span>
                        </div>
                        <div className="l2-usage-meter" aria-hidden="true">
                            {Array.from({ length: usage.limit ?? 0 }, (_, i) => (
                                <span
                                    key={i}
                                    className={`l2-usage-slot${
                                        i < usageUsed ? " l2-usage-slot--filled" : ""
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="l2-stat-sub">
                            {usageExhausted
                                ? `New unit unlocks in ${resetIn}.`
                                : usage.resetAt
                                  ? `${usageRemaining} left · oldest frees up in ${resetIn}.`
                                  : `${usageRemaining} new units available right now.`}
                        </div>
                        <a className="l2-usage-cta" href="/plans">
                            Get unlimited units
                            <ArrowRight />
                        </a>
                    </div>
                )}

                {/* Streak card */}
                <div className="l2-stat-card l2-stat-card--streak">
                    <div className="l2-stat-card-header">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 23c-3.6 0-8-3.14-8-8.73C4 9.2 8.67 3.17 11.2 1.06a1 1 0 0 1 1.6 0C15.33 3.17 20 9.2 20 14.27 20 19.86 15.6 23 12 23Zm0-4c1.5 0 3-1.12 3-3.09 0-1.8-1.67-4.3-2.8-5.48a.27.27 0 0 0-.4 0C10.67 11.61 9 14.11 9 15.91 9 17.88 10.5 19 12 19Z" />
                        </svg>
                        <span>Streak</span>
                    </div>
                    <div className="l2-stat-big">{streak}</div>
                    <div className="l2-stat-sub">
                        {streak === 1 ? "day in a row" : "days in a row"}
                    </div>
                    <div className="l2-week">
                        {WEEK_LABELS.map((d, i) => (
                            <div
                                key={i}
                                className={`l2-week-cell ${
                                    weekActive[i] ? "l2-week-cell--done" : ""
                                }`}
                                title={weekActive[i] ? "Practiced" : "No practice"}
                            >
                                {d}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Daily goal */}
                <div className="l2-stat-card l2-stat-card--goal">
                    <div className="l2-stat-card-header">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                        </svg>
                        <span>Daily Goal</span>
                    </div>
                    <div className="l2-goal-row">
                        <span className="l2-goal-nums">
                            {xpToday.toLocaleString()}
                            <span className="l2-goal-total">/{xpDailyGoal.toLocaleString()} XP</span>
                        </span>
                        <span className="l2-goal-pct">
                            {goalPct}%
                        </span>
                    </div>
                    <div className="l2-goal-track">
                        <div
                            className="l2-goal-fill"
                            style={{ width: `${goalPct}%` }}
                        />
                    </div>
                    <div className="l2-stat-sub">
                        {goalPct >= 100
                            ? "Goal hit. Nice work!"
                            : "Keep practicing to hit today's goal."}
                    </div>
                </div>

                {/* Level card */}
                <div className="l2-stat-card l2-stat-card--level">
                    <div className="l2-stat-card-header">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z" />
                        </svg>
                        <span>Level {level}</span>
                    </div>
                    <div className="l2-goal-row">
                        <span className="l2-goal-nums">
                            {totalXp.toLocaleString()}
                            <span className="l2-goal-total"> total XP</span>
                        </span>
                    </div>
                    <div className="l2-goal-track">
                        <div
                            className="l2-goal-fill l2-goal-fill--level"
                            style={{ width: `${levelPct}%` }}
                        />
                    </div>
                    <div className="l2-stat-sub">
                        {Math.max(0, xpForNext - xpIntoLevel).toLocaleString()} XP to Level {level + 1}
                    </div>
                </div>

                {/* Mini summary */}
                <div className="l2-mini-summary">
                    <div>
                        <div className="l2-mini-label">Lessons done</div>
                        <div className="l2-mini-value">{totalLessonsComplete}</div>
                    </div>
                    <div>
                        <div className="l2-mini-label">Units</div>
                        <div className="l2-mini-value">{totalUnitsListed}</div>
                    </div>
                </div>
            </aside>
        </div>
    );
}

/* ── Icons ── */
function CheckIcon() {
    return (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}
function StarIcon() {
    return (
        <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z" />
        </svg>
    );
}
function PlayIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <polygon points="6 4 20 12 6 20 6 4" />
        </svg>
    );
}
function BookIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
    );
}
function QuizIcon() {
    // Question mark inside a soft-edged circle — reads as "quiz" at a glance,
    // unlike the previous lightning bolt which read as "speed/power".
    return (
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M9.6 9.2a2.5 2.5 0 1 1 3.4 2.3c-.7.3-1 .9-1 1.6V14" />
            <circle cx="12" cy="17.2" r="0.6" fill="currentColor" stroke="none" />
        </svg>
    );
}
function LockIcon() {
    return (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
    );
}
function LimitIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
        </svg>
    );
}

/* Tree-row chevron — points right, rotates to point down when expanded. */
function CaretRight() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 6 15 12 9 18" />
        </svg>
    );
}

/* CTA arrow on the Start button. */
function ArrowRight() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="13 5 20 12 13 19" />
        </svg>
    );
}

/* Picks the right state icon for a lesson row. */
function LessonStateIcon({
    state,
    lesson,
}: {
    state: LessonState;
    lesson: LandingLesson;
}) {
    if (state === "complete") return <CheckIcon />;
    if (state === "current") return <StarIcon />;
    if (state === "locked") return <LockIcon />;
    if (lesson.isText) return <BookIcon />;
    if (lesson.isQuiz) return <QuizIcon />;
    return <PlayIcon />;
}
